import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCLoader } from 'rollup-allspark';
import KTModal from '../../../components/Drawer';
import { formatDate } from '../../../utils/format-date';
import { updateCashActivity, deleteCashActivity } from '../actions';
import * as selectors from '../selectors';

const ACTIVITY_TYPE_LABEL = {
  start_cash: 'Start Cash',
  cash_in: 'Cash In',
  cash_out: 'Cash Out',
  cash_sales: 'Cash Sales',
  change: 'Change',
  end_cash: 'End Cash',
};

const SALES_DESCRIPTION_PREFIX = /^Manual order #/i;

const deriveStatus = activity => {
  const rawType = (activity.type || '').toLowerCase();
  const description = activity.description || '';
  if (rawType === 'cash_in' && SALES_DESCRIPTION_PREFIX.test(description)) return 'cash_sales';
  if (ACTIVITY_TYPE_LABEL[rawType]) return rawType;
  if ((activity.category_name || '').toLowerCase().includes('change')) return 'change';
  return rawType || 'unknown';
};

const STATUS_PILL_CLASS = {
  start_cash: 'bg-grey-light text-black',
  cash_in: 'bg-green-lighter text-green-dark',
  cash_sales: 'bg-green-lighter text-green-dark',
  cash_out: 'bg-red-lighter text-red-dark',
  change: 'bg-yellow-lighter text-yellow-dark',
  end_cash: 'bg-grey-light text-black',
  unknown: 'bg-grey-light text-grey-dark',
};


const StatusPill = ({ status }) => {
  const label = ACTIVITY_TYPE_LABEL[status] || status;
  const cls = STATUS_PILL_CLASS[status] || STATUS_PILL_CLASS.unknown;
  return (
    <span className={`inline-block text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
};

StatusPill.propTypes = { status: PropTypes.string.isRequired };

const CashShiftDrawer = ({
  isOpen,
  onClose,
  shiftId,
  cashShiftActivities,
  cashShiftActivitiesLoading,
  cashActivityUpdating,
  cashActivityDeleting,
  onUpdateActivity,
  onDeleteActivity,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const startEdit = activity => {
    setEditingId(activity.id);
    setEditForm({ amount: activity.amount || '' });
    setDeletingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = activity => {
    onUpdateActivity({
      id: activity.id,
      category_id: activity.category_id,
      amount: parseFloat(editForm.amount) || 0,
      description: activity.description,
      activity_time: activity.activity_time,
      shiftId,
    });
    setEditingId(null);
  };

  const confirmDelete = id => {
    setDeletingId(id);
    setEditingId(null);
  };

  const cancelDelete = () => setDeletingId(null);

  const handleDelete = id => {
    onDeleteActivity(id, shiftId);
    setDeletingId(null);
  };

  let body = null;

  if (cashShiftActivitiesLoading) {
    body = (
      <div className="flex items-center justify-center" style={{ minHeight: 240 }}>
        <SCLoader lineWidth={5} diameter={50} />
      </div>
    );
  } else if (!cashShiftActivities || !cashShiftActivities.length) {
    body = (
      <div className="p-8 text-center text-sm text-grey-dark">
        No activities found for this shift
      </div>
    );
  } else {
    body = (
      <table className="table-auto w-full">
        <thead>
          <tr>
            <th className="px-4 py-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker text-left">Status</th>
            <th className="px-4 py-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker text-left">Activity</th>
            <th className="px-4 py-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker text-left">Amount</th>
            <th className="px-4 py-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker text-left">Date and Time</th>
            <th className="px-4 py-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker text-left w-20">Actions</th>
          </tr>
        </thead>
        <tbody>
          {cashShiftActivities.map((activity, index) => {
            const status = deriveStatus(activity);
            const isEditing = editingId === activity.id;
            const isDeleting = deletingId === activity.id;

            if (isEditing) {
              return (
                <tr key={activity.id || index} className="border-b border-grey-light bg-yellow-lightest">
                  <td className="px-4 py-3 align-top">
                    <StatusPill status={status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-black">{activity.description || '-'}</td>
                  <td className="px-4 py-3">
                    <input
                      className="border border-grey rounded px-2 py-1 text-sm w-28"
                      type="number"
                      value={editForm.amount}
                      onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="Amount"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-black whitespace-nowrap">
                    {activity.activity_time || activity.created_at
                      ? formatDate(activity.activity_time || activity.created_at, 'yyyy-MM-dd HH:mm')
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-x-2">
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(activity)}
                        disabled={cashActivityUpdating}
                        className="text-xs text-white bg-primary hover:bg-primary-dark rounded px-2 py-1 disabled:opacity-50"
                      >
                        {cashActivityUpdating ? '...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-xs text-grey-darker hover:text-black"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }

            if (isDeleting) {
              return (
                <tr key={activity.id || index} className="border-b border-grey-light bg-red-lightest">
                  <td colSpan={4} className="px-4 py-3 text-sm text-red-dark">
                    Delete this activity? This will update the shift balance.
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-x-2">
                      <button
                        type="button"
                        onClick={() => handleDelete(activity.id)}
                        disabled={cashActivityDeleting}
                        className="text-xs text-white bg-red rounded px-2 py-1 disabled:opacity-50"
                      >
                        {cashActivityDeleting ? '...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelDelete}
                        className="text-xs text-grey-darker hover:text-black"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={activity.id || index} className="border-b border-grey-light hover:bg-grey-lightest">
                <td className="px-4 py-3 align-top">
                  <StatusPill status={status} />
                </td>
                <td className="px-4 py-3 text-sm text-black">
                  {activity.description || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-black whitespace-nowrap">
                  {activity.amount != null ? activity.amount.toLocaleString() : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-black whitespace-nowrap">
                  {activity.activity_time || activity.created_at
                    ? formatDate(activity.activity_time || activity.created_at, 'yyyy-MM-dd HH:mm')
                    : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-x-2">
                    <button
                      type="button"
                      onClick={() => startEdit(activity)}
                      title="Edit"
                      className="text-grey-darker hover:text-primary"
                    >
                      <i className="kt-edit text-sm" />
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmDelete(activity.id)}
                      title="Delete"
                      className="text-grey-darker hover:text-red"
                    >
                      <i className="kt-trash text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <KTModal anchor="right" isOpen={isOpen} onClose={onClose} className="bg-white h-full overflow-y-auto">
      <div className="flex flex-col h-full" style={{ width: 640 }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey">
          <div className="text-lg font-medium">Shift #{shiftId || ''}</div>
          <div className="flex items-center gap-x-6">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close drawer"
              className="flex items-center justify-center w-8 h-8 rounded-full text-grey-dark hover:text-black hover:bg-grey-lighter text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">{body}</div>
      </div>
    </KTModal>
  );
};

CashShiftDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shiftId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cashShiftActivities: PropTypes.array,
  cashShiftActivitiesLoading: PropTypes.bool,
  cashActivityUpdating: PropTypes.bool,
  cashActivityDeleting: PropTypes.bool,
  onUpdateActivity: PropTypes.func,
  onDeleteActivity: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  cashShiftActivities: selectors.makeSelectCashShiftActivities(),
  cashShiftActivitiesLoading: selectors.makeSelectCashShiftActivitiesLoading(),
  cashActivityUpdating: selectors.makeSelectCashActivityUpdating(),
  cashActivityDeleting: selectors.makeSelectCashActivityDeleting(),
});

const mapDispatchToProps = dispatch => ({
  onUpdateActivity: payload => dispatch(updateCashActivity(payload)),
  onDeleteActivity: (id, shiftId) => dispatch(deleteCashActivity(id, shiftId)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(CashShiftDrawer);
